import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Bot } from "lucide-react";

interface HealthArea {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  display_order: number;
}

interface AiService {
  id: string;
  name: string;
  provider: string;
  model_code: string;
  is_active: boolean;
}

interface AiConfiguration {
  ai_action_type: string;
  ai_service_id: string;
}

const AI_ACTION_TYPES = [
  { value: 'smartfile_extraction', label: 'IA Smartfile Extração', selectable: false },
  { value: 'smartfile_categorization', label: 'IA Smartfile Categorização', selectable: false },
  { value: 'smartfile_output_json', label: 'IA Smartfile Output JSON', selectable: false },
  { value: 'smartfile_summary', label: 'IA Smartfile Resumo/Insights', selectable: true },
  { value: 'global_analysis', label: 'IA Análise Global', selectable: true },
  { value: 'conversation', label: 'IA Conversação', selectable: true },
] as const;

export function HealthAreasManagement() {
  const { toast } = useToast();
  const [areas, setAreas] = useState<HealthArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showAiConfigDialog, setShowAiConfigDialog] = useState(false);
  const [editingArea, setEditingArea] = useState<HealthArea | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingArea, setDeletingArea] = useState<HealthArea | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [aiServices, setAiServices] = useState<AiService[]>([]);
  const [aiConfigurations, setAiConfigurations] = useState<Record<string, string>>({});
  const [loadingAiConfig, setLoadingAiConfig] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: true,
    display_order: 0,
  });

  useEffect(() => {
    loadAreas();
    loadAiServices();
  }, []);

  async function loadAreas() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("health_areas")
        .select("*")
        .order("display_order");

      if (error) throw error;
      setAreas(data || []);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar áreas",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadAiServices() {
    try {
      const { data, error } = await supabase
        .from("ai_services")
        .select("id, name, provider, model_code, is_active")
        .eq("is_active", true)
        .order("provider, model_code");

      if (error) {
        console.error("Error loading AI services:", error);
        toast({
          variant: "destructive",
          title: "Erro ao carregar modelos",
          description: error.message,
        });
        return;
      }
      
      console.log("AI Services loaded:", data?.length || 0, "services", data);
      setAiServices(data || []);
    } catch (error) {
      console.error("Error loading AI services:", error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar modelos",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    }
  }

  async function loadAiConfigurations(healthAreaId: string) {
    try {
      setLoadingAiConfig(true);
      const { data, error } = await supabase
        .from("health_area_ai_configurations")
        .select("ai_action_type, ai_service_id")
        .eq("health_area_id", healthAreaId);

      if (error) throw error;

      const configMap: Record<string, string> = {};
      (data || []).forEach((config) => {
        configMap[config.ai_action_type] = config.ai_service_id;
      });
      setAiConfigurations(configMap);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar configurações de IA",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    } finally {
      setLoadingAiConfig(false);
    }
  }

  async function handleOpenAiConfig(area: HealthArea) {
    setEditingArea(area);
    setShowAiConfigDialog(true);
    // Garantir que os serviços de IA estão carregados
    await loadAiServices();
    await loadAiConfigurations(area.id);
  }

  async function handleSaveAiConfigurations() {
    if (!editingArea) return;

    try {
      // Salvar cada configuração
      for (const actionType of AI_ACTION_TYPES) {
        const serviceId = aiConfigurations[actionType.value];
        if (!serviceId) continue;

        const { error } = await supabase
          .from("health_area_ai_configurations")
          .upsert({
            health_area_id: editingArea.id,
            ai_action_type: actionType.value,
            ai_service_id: serviceId,
          }, {
            onConflict: "health_area_id,ai_action_type"
          });

        if (error) throw error;
      }

      toast({
        title: "Configurações salvas",
        description: "As configurações de IA foram salvas com sucesso.",
      });

      setShowAiConfigDialog(false);
      setEditingArea(null);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar configurações",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    }
  }

  function handleAdd() {
    setEditingArea(null);
    setFormData({
      name: "",
      description: "",
      is_active: true,
      display_order: areas.length + 1,
    });
    setShowDialog(true);
  }

  function handleEdit(area: HealthArea) {
    setEditingArea(area);
    setFormData({
      name: area.name,
      description: area.description || "",
      is_active: area.is_active,
      display_order: area.display_order,
    });
    setShowDialog(true);
  }

  async function handleSave() {
    try {
      if (editingArea) {
        const { error } = await supabase
          .from("health_areas")
          .update(formData)
          .eq("id", editingArea.id);

        if (error) throw error;

        toast({
          title: "Área atualizada",
          description: "A área da saúde foi atualizada com sucesso.",
        });
      } else {
        const { data, error } = await supabase
          .from("health_areas")
          .insert(formData)
          .select()
          .single();

        if (error) throw error;

        // Criar configurações padrão de IA para a nova área
        if (data?.id) {
          await createDefaultAiConfigurations(data.id);
        }

        toast({
          title: "Área criada",
          description: "A área da saúde foi criada com sucesso.",
        });
      }

      setShowDialog(false);
      loadAreas();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    }
  }

  async function createDefaultAiConfigurations(healthAreaId: string) {
    try {
      // Buscar IDs das IAs padrão
      const { data: services } = await supabase
        .from("ai_services")
        .select("id, name")
        .in("name", [
          "IA Smartfile Extração",
          "IA Smartfile Categorização",
          "IA Smartfile Output JSON",
          "IA Smartfile Resumo",
          "IA Análise Global",
          "IA Conversação",
        ]);

      if (!services || services.length === 0) return;

      const serviceMap: Record<string, string> = {};
      services.forEach((service) => {
        serviceMap[service.name] = service.id;
      });

      const configs = [
        { action: "smartfile_extraction", service: serviceMap["IA Smartfile Extração"] },
        { action: "smartfile_categorization", service: serviceMap["IA Smartfile Categorização"] },
        { action: "smartfile_output_json", service: serviceMap["IA Smartfile Output JSON"] },
        { action: "smartfile_summary", service: serviceMap["IA Smartfile Resumo"] },
        { action: "global_analysis", service: serviceMap["IA Análise Global"] },
        { action: "conversation", service: serviceMap["IA Conversação"] },
      ].filter((config) => config.service);

      if (configs.length > 0) {
        const { error } = await supabase
          .from("health_area_ai_configurations")
          .insert(
            configs.map((config) => ({
              health_area_id: healthAreaId,
              ai_action_type: config.action,
              ai_service_id: config.service,
            }))
          );

        if (error) {
          console.error("Error creating default AI configurations:", error);
        }
      }
    } catch (error) {
      console.error("Error creating default AI configurations:", error);
    }
  }

  async function handleToggleActive(area: HealthArea) {
    try {
      const { error } = await supabase
        .from("health_areas")
        .update({ is_active: !area.is_active })
        .eq("id", area.id);

      if (error) throw error;

      toast({
        title: area.is_active ? "Área desativada" : "Área ativada",
        description: `A área "${area.name}" foi ${area.is_active ? "desativada" : "ativada"}.`,
      });

      loadAreas();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    }
  }

  async function checkAreaUsage(areaId: string): Promise<number> {
    const { count, error } = await supabase
      .from("profiles")
      .select("*", { count: "exact", head: true })
      .eq("health_area_id", areaId);

    if (error) throw error;
    return count || 0;
  }

  async function handleDeleteClick(area: HealthArea) {
    try {
      setDeleteError(null);
      const profCount = await checkAreaUsage(area.id);

      if (profCount > 0) {
        setDeleteError(
          `Não é possível excluir esta área pois ${profCount} profissional(is) está(ão) vinculado(s) a ela.`
        );
      }

      setDeletingArea(area);
      setShowDeleteDialog(true);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao verificar área",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    }
  }

  async function confirmDelete() {
    if (!deletingArea || deleteError) return;

    try {
      const { error } = await supabase
        .from("health_areas")
        .delete()
        .eq("id", deletingArea.id);

      if (error) throw error;

      toast({
        title: "Área excluída",
        description: `A área "${deletingArea.name}" foi excluída com sucesso.`,
      });

      setShowDeleteDialog(false);
      setDeletingArea(null);
      loadAreas();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao excluir",
        description: error instanceof Error ? error.message : "Erro desconhecido",
      });
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Áreas da Saúde</CardTitle>
              <CardDescription>
                Gerencie as áreas da saúde disponíveis para os profissionais
              </CardDescription>
            </div>
            <Button onClick={handleAdd}>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Área
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ordem</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {areas.map((area) => (
                <TableRow key={area.id}>
                  <TableCell>{area.display_order}</TableCell>
                  <TableCell className="font-medium">{area.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {area.description || "-"}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={area.is_active}
                      onCheckedChange={() => handleToggleActive(area)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenAiConfig(area)}
                        title="Configurar IAs"
                      >
                        <Bot className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(area)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(area)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingArea ? "Editar Área" : "Nova Área"}
            </DialogTitle>
            <DialogDescription>
              {editingArea
                ? "Atualize os dados da área da saúde"
                : "Adicione uma nova área da saúde"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Ex: Medicina"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Ex: Médicos e especialistas"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_order">Ordem de Exibição</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData({ ...formData, display_order: parseInt(e.target.value) })
                }
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_active: checked })
                }
              />
              <Label htmlFor="is_active">Área ativa</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              {editingArea ? "Atualizar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAiConfigDialog} onOpenChange={setShowAiConfigDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configurar IAs - {editingArea?.name}</DialogTitle>
            <DialogDescription>
              Selecione o modelo de IA para cada ação. Todos os modelos disponíveis podem ser selecionados.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {loadingAiConfig && aiServices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Carregando modelos disponíveis...
              </div>
            ) : aiServices.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-2">
                  Nenhum modelo de IA disponível.
                </p>
                <p className="text-sm text-muted-foreground">
                  Crie modelos na página de Configurações de IA primeiro.
                </p>
              </div>
            ) : (
              AI_ACTION_TYPES.map((actionType) => {
                const currentServiceId = aiConfigurations[actionType.value];
                const currentService = currentServiceId
                  ? aiServices.find((s) => s.id === currentServiceId)
                  : null;
                const isSelectable = actionType.selectable;
                const displayValue = currentService
                  ? `${currentService.name} (${currentService.provider}/${currentService.model_code})`
                  : currentServiceId
                  ? "Carregando..."
                  : "Não configurado";

                return (
                  <div key={actionType.value} className="space-y-2">
                    <Label htmlFor={actionType.value}>
                      {actionType.label}
                      {!isSelectable && (
                        <span className="text-xs text-muted-foreground ml-2">(Fixo)</span>
                      )}
                    </Label>
                    {isSelectable ? (
                      <Select
                        value={currentServiceId || ""}
                        onValueChange={(value) => {
                          setAiConfigurations({
                            ...aiConfigurations,
                            [actionType.value]: value,
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um modelo" />
                        </SelectTrigger>
                        <SelectContent>
                          {aiServices.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.name} ({service.provider}/{service.model_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={displayValue}
                        disabled={true}
                        className="bg-muted cursor-not-allowed"
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAiConfigDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveAiConfigurations} disabled={loadingAiConfig}>
              {loadingAiConfig ? "Salvando..." : "Salvar Configurações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteError ? (
                <div className="text-destructive font-medium">{deleteError}</div>
              ) : (
                <>
                  Tem certeza que deseja excluir a área <strong>"{deletingArea?.name}"</strong>?
                  <br />
                  Esta ação não pode ser desfeita.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {!deleteError && (
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-destructive hover:bg-destructive/90"
              >
                Excluir
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
